import { Clock, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';

import { formatDuration } from '../../../../chrome/form';
import { cn } from '../../../../ui/utils';
import { profileCatalogue } from './messages';
import { compactDuration, periodSwatch, swatchVars, type PeriodDraft } from './periods';

const SPOKEN = ['minutes', 'hours', 'days'] as const;

function spoken(seconds: number | null): string {
  return seconds === null ? '—' : formatDuration(seconds, [...SPOKEN]);
}

function short(seconds: number | null): string {
  return seconds === null ? '—' : compactDuration(seconds);
}

/** `<refresh> 5m <clock> 12h`, painted in the period's colour. `frequencyOnly` draws a
 *  story's single interval. */
export function PeriodSummary(props: {
  period: PeriodDraft;
  frequencyOnly?: boolean;
  className?: string;
}): ReactNode {
  const { period } = props;
  const label = props.frequencyOnly
    ? profileCatalogue.t('period.summary.frequencyOnly', {
        frequency: spoken(period.frequency_seconds),
      })
    : profileCatalogue.t('period.summary', {
        frequency: spoken(period.frequency_seconds),
        duration: spoken(period.duration_seconds),
      });
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={swatchVars(periodSwatch(period))}
      className={cn(
        'inline-flex min-w-0 items-center gap-1 overflow-hidden rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap tabular-nums',
        props.className,
      )}
    >
      <RefreshCw aria-hidden className="size-3 shrink-0" />
      {short(period.frequency_seconds)}
      {!props.frequencyOnly && (
        <>
          <Clock aria-hidden className="ml-1 size-3 shrink-0" />
          {short(period.duration_seconds)}
        </>
      )}
    </span>
  );
}
